import { useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/router'

import classnames from 'classnames'

import { Icon } from '~/components/atoms/Icon/Icon'
import LoadSpinner from '~/components/atoms/LoadSpinner/LoadSpinner'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { useDebounceFn } from '~/hooks/useDebounceFn'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { getRouterPath } from '~/utils/getRouterPath'
import { useScrollPosition } from '~/utils/useScrollPosition'

import styles from './SearchLozenge.module.css'

export interface SearchLozengeProps {
  handleResults: (query: string) => void
  isLoading?: boolean
  placeholder?: string
  className?: string
  value?: string
  artistName?: string
  testID?: string
  filterPosition: number | null
}

export default function SearchLozenge({
  handleResults,
  isLoading = false,
  placeholder,
  className,
  value,
  testID,
  artistName,
  filterPosition,
}: SearchLozengeProps) {
  const router = useRouter()

  const [isOpen, setIsOpen] = useState(false)
  const [canClose, setCanClose] = useState(false)
  const [isNotSticky, setIsNotSticky] = useState(true)

  const input = useRef<HTMLInputElement>(null)
  const [currentValue, setCurrentValue] = useState<string | undefined>(value)
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)

  useScrollPosition(
    ({ currPos }) => {
      setIsNotSticky(currPos.y > Number(`-${filterPosition}`))
    },
    [filterPosition]
  )

  const scrollPageToSearch = () => {
    // Scroll element to top
    // Scroll should be 52 when fixing goes live
    const inputTop = input.current
      ? input.current.getBoundingClientRect().top +
        document.documentElement.scrollTop -
        52
      : 0
    window.scrollTo({
      top: inputTop,
      behavior: 'smooth',
    })
  }

  const handleFocus = () => {
    if (input.current && !isOpen) {
      input.current.focus()
      setIsOpen(true)

      // setTimeout(() => {
      //   scrollPageToSearch()
      // }, 400)
    }
  }

  const handleClose = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(false)
    if (input.current) input.current.blur()
  }

  useEffect(() => {
    setIsOpen(false)
    if (input.current) input.current.blur()
    setCurrentValue('')
    setCanClose(false)
    handleResults('')
  }, [artistName])

  const _pushBrowserUrl = (clear?: boolean) => {
    const pathname = getRouterPath(router)
    const isArtworkPage = router.asPath.indexOf('artworks') !== -1
    const page = getRouterPath(router).split('/').pop()

    const routerParams = {} as any
    const queryVal =
      input.current && input.current.value.length > 0
        ? input.current.value
        : undefined

    delete router.query['lang']

    const routerQueryParams = queryVal
      ? { ...router.query, aw: queryVal }
      : { ...router.query }

    if (clear || !queryVal) delete routerQueryParams?.aw

    routerParams['query'] = routerQueryParams
    routerParams['pathname'] = pathname

    const path = routerQueryParams
      ? {
          pathname,
          query: { ...routerQueryParams },
        }
      : {
          pathname,
        }

    const artistPath = routerQueryParams
      ? {
          pathname: pathname,
          query: { slug: page, ...routerQueryParams },
        }
      : {
          pathname: pathname,
          query: { slug: page },
        }

    router.push(isArtworkPage ? path : artistPath, undefined, {
      shallow: true,
    })
  }
  const pushBrowserUrl = useDebounceFn(_pushBrowserUrl, 500)

  const handleValue = (val: string) => {
    setCurrentValue(val)
    setCanClose(!!val)
    // This might be the point to remove
    setIsOpen(!!val)
    handleResults(val)
  }

  useEffect(() => {
    if (value) {
      handleValue(value)
    } else {
      setCurrentValue(value)

      if (input.current) {
        input?.current.blur()
        handleValue('')
      }
    }
  }, [value])

  useEffect(() => {
    const locationQuery = location?.search?.match(/aw=([^&]*)/)
    const passedValue: string = locationQuery
      ? decodeURI(locationQuery[1])
      : (value as string)
    handleValue(passedValue)
  }, [])

  const handleChange = (e: any) => {
    const val = e.target.value
    setCanClose(val.length > 0)
    handleResults(e.currentTarget.value)
    pushBrowserUrl(!val)
  }

  const handleEnter = (e: any) => {
    if (e.key === 'Enter') e.target.blur()
  }

  return (
    <div
      className={classnames(
        styles.base,
        !isNotSticky && isMobile && styles.baseSticky,
        className && className,
        ((!isNotSticky && isOpen) || isNotSticky) && styles.searchOpen,
        isOpen && styles.searchFocus,
        !isNotSticky && isOpen && styles.searchStickyOpen,
        isNotSticky && styles.searchDefaultOpen,
        currentValue && !isOpen && styles.searchWithValue
      )}
      data-testid={testID}
      id="searchautocomplete"
      onClick={handleFocus}
    >
      <Icon type="Magnify" width={15} height={15} responsive={false} />
      <div className={styles.searchField}>
        <input
          ref={input}
          type="text"
          onChange={e => handleChange(e)}
          onKeyUp={handleEnter}
          className={classnames(styles.input, 'searchInput')}
          value={currentValue}
          onFocus={handleFocus}
          placeholder={placeholder}
        />

        <div
          role="button"
          className={classnames(styles.closeButton, {
            [styles['canClose']]: canClose,
          })}
          onClick={() => {
            handleValue('')
            pushBrowserUrl(true)
          }}
        >
          <span className={styles.cross}>×</span>
        </div>
        {isLoading && (
          <div className={styles.loadSpinner}>
            <LoadSpinner />
          </div>
        )}
        {placeholder && !input?.current?.value && (
          <Text
            size={TextSizes.bodyMed}
            Tag="label"
            className={classnames(styles.label, {
              // [styles.notEmpty]: isOpen,
            })}
          >
            {placeholder}
          </Text>
        )}

        {isOpen && !isNotSticky && !input?.current?.value && (
          <div
            role="button"
            className={classnames(styles.closeButton, {
              [styles['canClose']]: !isMobile
                ? input?.current?.value && input?.current?.value.length > 0
                : true,
            })}
            onClick={e => handleClose(e)}
          >
            <span className={styles.cross}>×</span>
          </div>
        )}
      </div>
    </div>
  )
}
